import { OpenObserveLogger } from './shared/together/logger/open-observe.logger';
import firebase from 'firebase/app';
import { NgModule, APP_INITIALIZER, ErrorHandler, Injectable } from '@angular/core';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { HammerGestureConfig, HammerModule } from '@angular/platform-browser';
import { BrowserModule, HAMMER_GESTURE_CONFIG } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';
import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AbstractLogger, DeviceConsoleLogger } from '@mobilejazz/harmony-core';
import { CommonModule, UserService } from '@together/common';
import { isPlatform } from '@ionic/core';
import player from 'lottie-web';
import * as Hammer from 'hammerjs';

import { environment } from '@env/environment';
import { versions } from '@env/versions';
import { PaymentProviderType } from '@env/types';

import { AppComponent } from './app.component';
import { AppErrorHandler } from './app.error-handler';
import { AppRoutingModule } from './app-routing.module';
import { SharedModule } from './shared/shared.module';
import togetherIcons from '../assets/icons/icons';
import { provideLottieOptions } from 'ngx-lottie';
import { Capacitor } from '@capacitor/core';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@Injectable()
export class MyHammerConfig extends HammerGestureConfig {
    overrides = {
        swipe: { direction: Hammer.DIRECTION_HORIZONTAL },
        pinch: { enable: false },
        rotate: { enable: false },
    };
}

// Dynamically decide what payment provider to use
function getPaymentProvider(): PaymentProviderType {
    const isNative = Capacitor.isNativePlatform();
    return isNative ? PaymentProviderType.NativeStore : PaymentProviderType.Stripe;
}

// Add Stripe script
if (getPaymentProvider() === PaymentProviderType.Stripe) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://js.stripe.com/v3/';
    document.head.appendChild(script);
}

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        AppRoutingModule,
        BrowserModule,
        BrowserAnimationsModule,
        HammerModule,
        CommonModule.forRoot({
            firebase: { storageBucket: environment.firebaseConfig.storageBucket },
            googleOAuthClientId: environment.googleOAuthClientId,
            loggedInLandingURL: '/home',
            loginURL: '',
            revision: versions.revision,
            version: versions.version,
        }),
        FontAwesomeModule,
        IonicModule.forRoot(),
        SharedModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
    ],
    providers: [
        { provide: 'PaymentProviderType', useValue: getPaymentProvider() },
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        {
            provide: ErrorHandler,
            useFactory: (logger: AbstractLogger) => new AppErrorHandler(logger, environment.production),
            deps: [AbstractLogger],
        },
        {
            provide: AbstractLogger,
            useFactory: (http: HttpClient) => {
                return new OpenObserveLogger(http);
            },
            deps: [HttpClient],
        },
        {
            provide: HAMMER_GESTURE_CONFIG,
            useClass: MyHammerConfig,
        },
        {
            // Wait until the RemoteConfig is loaded before continuing with app initialization
            provide: APP_INITIALIZER,
            multi: true,
            deps: [AbstractLogger, 'FirebaseRemoteConfig', UserService],
            useFactory: (
                logger: AbstractLogger,
                remoteConfig: firebase.remoteConfig.RemoteConfig,
                userService: UserService,
            ) => {
                logger.info('AppModule', 'APP_INITIALIZER');
                // Must return a function that returns a promise
                // See: https://stackoverflow.com/a/56165556/379923
                return async () => {
                    if (!environment.production) {
                        remoteConfig.setLogLevel('debug');
                    }
                    await Promise.all([remoteConfig.fetchAndActivate(), userService.init()]);
                    //Update environment variables from remote config
                    try {
                        environment.privacyPolicyURL = remoteConfig.getString('privacyPolicyURL');
                        environment.termsOfServiceURL = remoteConfig.getString('termsOfUseURL');
                        environment.permissionsURL =
                            remoteConfig.getString('permissionsURL') || environment.permissionsURL;
                        environment.uploadBooksTutorialURL =
                            remoteConfig.getString('uploadBooksTutorialURL') || environment.uploadBooksTutorialURL;
                        environment.purchaseFaqsURL =
                            remoteConfig.getString('bookstoreExplainerURL') || environment.purchaseFaqsURL;
                        environment.giftExplainerURL =
                            remoteConfig.getString('giftExplainerURL') || environment.giftExplainerURL;
                        environment.faqURL = remoteConfig.getString('faqURL') || environment.faqURL;
                        environment.openObserveConfig = JSON.parse(remoteConfig.getString('openObserveConfig'));
                        environment.giftDomainPrefix =
                            remoteConfig.getString('giftDomainPrefix') || environment.giftDomainPrefix;
                        environment.installDomainPrefix =
                            remoteConfig.getString('installDomainPrefix') || environment.installDomainPrefix;
                        environment.inviteDomainPrefix =
                            remoteConfig.getString('inviteDomainPrefix') || environment.inviteDomainPrefix;
                    } catch (e) {
                        logger.error('AppModule', `Error parsing remote config${e}`);
                    }
                    logger.info('AppModule', 'APP_INITIALIZER done');
                };
            },
        },
        provideHttpClient(withInterceptorsFromDi()),
        provideLottieOptions({
            player: () => import('lottie-web'),
        }),
    ],
})
export class AppModule {
    constructor(library: FaIconLibrary) {
        library.addIcons.apply(library, togetherIcons);
    }
}
